import { AccordionSection } from '@fox/atoms/src/accordion-section/index';
import { Button } from '@fox/atoms/src/button/index';
import { DeletePopover } from '@fox/atoms/src/popover/popovers';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { MuiAlertSnackbar } from '@fox/atoms/src/mui-snackbar/index';
import { Select } from '@fox/atoms/src/select/index';
import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { TextField } from '@fox/atoms/src/text-field/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { FlexRowSpaced } from '@fox/layout/src/flex';
import { DEFAULT_IMPORTER_PROFILE, useImportWizardContext } from './import-wizard';
import { tryMatchColumns } from './sheet-utils';
import { ImportProfile, Sheet } from './types';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const FIELD_INPUT_WIDTH = '25rem';

export function ProfileSection({ isCustomMatch, ws }: { isCustomMatch: boolean; ws: Sheet }) {
	const {
		state: { matchAlgorithm, customMatchKeys },
		dispatch,
		mapRef,
		setMapping,
		mapTypes,
		category,
		profile,
		setProfile,
		createImportProfile,
		deleteImportProfile,
		getImportProfiles
	} = useImportWizardContext() ?? {};
	const [profilesList, setProfilesList] = useState<ImportProfile[]>([]);

	const queryKey = ['profile', category] as const;

	const { refetch } = useQuery({
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		queryFn: async function validateAndMatch(args) {
			const [_, cat] = args.queryKey;
			const res = await getImportProfiles?.(cat);
			if (res) {
				setProfilesList(res);
			}

			return true;
		},
		queryKey
		//enabled: enableProfileSettings
	});
	const profileIsDefault = profile === DEFAULT_IMPORTER_PROFILE;

	const createProfile = async (name: string) => {
		const newMapping = [];
		Object.entries(mapRef).forEach(m => newMapping.push({ headerName: m[0], matchKey: m[1] }));
		try {
			const { id, ...p } = profile;
			await createImportProfile?.({
				...p,
				category: category,
				mapping: newMapping,
				name: name,
				custom: isCustomMatch ? customMatchKeys : null,
				match: matchAlgorithm
			});
			refetch();
		} catch (err) {
			console.error(`Error creating import mapping profile - ${err}`);
		}
	};
	const [mapName, setMapName] = useState<string | null>(null);
	const [snackbar, setSnackbar] = useState<boolean>(false);
	const noMapping = !mapRef || Object.keys(mapRef)?.length === 0;
	const profileExists = profilesList?.filter(p => p.name === mapName)?.length > 0;
	return (
		<AccordionSection
			details={
				<>
					<Typography variant="body1">Align the columns in your spreadsheet to match the right fields in SwiftFox</Typography>
					<Typography variant="h6">Select a saved profile of your mapped data</Typography>
					<FlexRowSpaced>
						<Select<ImportProfile, false, false, false>
							options={profilesList}
							value={profile?.mapping ? profile : null}
							onChange={(_, value) => {
								if (value) {
									const header = ws.extractHeader(value?.hasHeader ?? true, value?.skipRowsStart);
									for (const mapping in mapRef) {
										setMapping(mapping, null);
									}
									setProfile(value);
									if (value.match) {
										dispatch({ type: 'SET_SEARCH', value: value.match, custom: value.custom });
									}
									tryMatchColumns(header, setMapping, mapTypes, mapRef, value);
								} else {
									// resetting profile on clear
									setProfile(DEFAULT_IMPORTER_PROFILE);
								}
							}}
							getOptionLabel={o => o.name}
							style={{ width: FIELD_INPUT_WIDTH }}
							fullWidth
							size="small"
							label="Apply an existing column mapping profile"
						/>

						{!profileIsDefault ? (
							<DeletePopover
								message={<Typography>This will delete {profile.name}, are you sure you want to continue?</Typography>}
								confirm={async () => {
									await deleteImportProfile({ id: profile.id, category: category });
									setProfile(DEFAULT_IMPORTER_PROFILE);
									refetch();
								}}
							>
								<div>
									<Button variant="secondary" color="error" title="Delete profile">
										Delete
									</Button>
								</div>
							</DeletePopover>
						) : null}
					</FlexRowSpaced>
					<Typography variant="h6">Create a new profile of your mapped data</Typography>
					<FlexRowSpaced>
						<TextField
							size="small"
							value={mapName ?? ''}
							style={{ width: FIELD_INPUT_WIDTH }}
							onChange={e => setMapName(e.target.value)}
							status={profileExists ? 'error' : null}
							label={'Profile name'}
						/>
						{!strIsNullOrEmpty(mapName) ? (
							<Button
								variant="primary"
								color="primary"
								size="small"
								title="Save mapping"
								disabled={profileExists || noMapping}
								onClick={async () => {
									await createProfile(String(mapName));
									setMapName(null);
									setSnackbar(true);
								}}
							>
								Save mapping
							</Button>
						) : null}
					</FlexRowSpaced>
					<MuiAlertSnackbar
						onClose={() => setSnackbar(false)}
						open={snackbar}
						message={'Mapping profile saved!'}
						severity="success"
					/>
					{profileExists ? <MuiAlert severity="warning">This profile already exists</MuiAlert> : null}
				</>
			}
			summaryTitle={'Map your data to the right place in SwiftFox'}
			defaultExpanded={true}
		/>
	);
}
